<template>
  <v-dialog v-model="$store.state.editProfile" :width="profileWidth" @update:modelValue="clear">
    <v-card class="py-4 px-2 px-md-6 text-N700">
      <v-card-title class="d-flex align-center pe-0">
        <h2 class="font-weight-bold">{{ $t('edit_profile') }}</h2>
        <v-spacer />
        <v-btn size="x-small" icon elevation="0">
          <img
            :src="require('@/assets/close-circle.svg')"
            alt="close-profile"
            @click="$store.commit('editProfileToggle')"
          />
        </v-btn>
      </v-card-title>

      <div>
        <v-tabs v-model="tab">
          <v-tab value="plans" v-if="is_admin">
            {{ $t('plans') }}
          </v-tab>
          <v-tab value="workspace">
            {{ $t('workspace') }}
          </v-tab>
          <v-tab value="profile">
            {{ $t('profile') }}
          </v-tab>
          <v-tab value="password">
            {{ $t('password_security') }}
          </v-tab>
        </v-tabs>
      </div>

      <v-card-text>
        <v-window v-model="tab" :touch="false">
          <v-window-item value="plans">
            <v-card
              class="d-flex align-start flex-column mx-8 my-4"
              height="100%"
              :class="{ 'on-hover': isHovering }"
              v-if="factory_info.plan"
              color="deep-purple-lighten-5 border"
            >
              <v-row>
                <v-col cols="12" md="6">
                  <v-card-title>
                    {{ factory_info.plan.name }}
                    <span class="text-N100 ml-2 mb-2">{{ $t('current_plan') }}</span>
                  </v-card-title>

                  <v-card-item class="text-N100">
                    {{ factory_info.plan.description }}
                  </v-card-item>

                  <v-card-item class="text-N600">
                    <strong class="font-weight-bold mt-5">{{ factory_info.plan.amount }}</strong>
                    {{ factory_info.plan.currency }} / {{ factory_info.plan.days }} {{ $t('days') }}
                  </v-card-item>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card-item class="mt-4">
                    <div
                      class="d-flex align-center my-4"
                      v-for="(option, index) in factory_info.plan.options"
                      :key="index"
                    >
                      <div class="mr-4">
                        <img :src="require('@/assets/check.svg')" />
                      </div>
                      <div class="text-N600">
                        {{ option }}
                      </div>
                    </div>
                  </v-card-item>
                </v-col>
              </v-row>
            </v-card>

            <v-row class="pa-8">
              <v-col
                cols="12"
                lg="4"
                v-for="plan in plans.filter((p) => !p.is_basic).sort((a, b) => (a.id > b.id ? 1 : -1))"
                :key="plan.id"
              >
                <v-hover v-slot="{ isHovering, props }">
                  <v-card
                    :elevation="isHovering ? 12 : 2"
                    class="pa-4 text-white h-100 d-flex flex-column border"
                    v-bind="props"
                    :color="isHovering ? 'deep-purple-lighten-5' : ''"
                    @click="upgrade_plan(plan)"
                    :loading="plan.loading"
                    :disabled="plan.is_basic || plan.loading"
                  >
                    <h2 class="font-weight-bold">
                      {{ plan.name }}
                    </h2>

                    <h4 class="text-N100 mt-4">
                      {{ plan.description }}
                    </h4>

                    <h3 class="text-N600 text-center mt-4">
                      <strong class="font-weight-bold mt-5">{{ plan.amount }}</strong>
                      {{ plan.currency }} / {{ plan.days }} {{ $t('days') }}
                    </h3>

                    <v-card-item>
                      <div class="d-flex align-center my-4" v-for="(option, index) in plan.options" :key="index">
                        <div class="mr-4">
                          <img v-if="plan.id === 1 && index === 1" :src="require('@/assets/uncheck.svg')" />
                          <img v-else :src="require('@/assets/check.svg')" />
                        </div>
                        <div class="text-N600">
                          {{ option }}
                        </div>
                      </div>
                    </v-card-item>

                    <div class="mt-auto w-100">
                      <v-btn
                        class="w-100"
                        size="large"
                        color="primary"
                        :loading="plan.loading"
                        :disabled="plan.is_basic || plan.loading"
                      >
                        <h5 class="font-weight-bold text-capitalize text-white">
                          {{ $t('upgrade_to') }} {{ plan.name }}
                        </h5>
                      </v-btn>
                    </div>
                  </v-card>
                </v-hover>
              </v-col>
            </v-row>
          </v-window-item>

          <v-window-item value="workspace" v-if="tab === 'workspace'">
            <h2 class="text-N70 mb-4 text-uppercase font-weight-bold">
              {{ $t('company_logo') }}
            </h2>

            <v-sheet class="d-flex">
              <div>
                <img v-if="factory_info.factory_logo" :src="factory_info.factory_logo" width="64" height="64" />
                <img v-else :src="require('@/assets/profile-circle.svg')" width="64" />
              </div>
              <div class="ms-3">
                {{ $t('max_file_size') }}: 5 MB
                <div class="d-md-flex mt-2">
                  <v-btn
                    variant="outlined"
                    color="primary"
                    class="text-capitalize mr-2 w-100"
                    @click="$refs.companyImage.click()"
                    :loading="factoryImageLoading"
                    :disabled="factoryImageLoading || removeCompanyImageLoading"
                  >
                    {{ $t('change_logo') }}
                  </v-btn>
                  <input
                    ref="companyImage"
                    style="display: none"
                    type="file"
                    accept="image/*"
                    @change="loadCompanyImage"
                  />
                  <v-btn
                    color="error"
                    class="text-capitalize mt-2 mt-md-0 w-100"
                    @click="removeComanyImage"
                    :loading="removeCompanyImageLoading"
                    :disabled="removeCompanyImageLoading || factoryImageLoading"
                  >
                    {{ $t('remove') }}
                  </v-btn>
                </div>
              </div>
            </v-sheet>

            <h2 class="text-N70 my-4 text-uppercase font-weight-bold">
              {{ $t('company_info') }}
            </h2>

            <v-row class="d-flex">
              <v-col cols="12" md="6">
                <CustomInput :title="$t('company_name')" v-model:value="companyName" readonly></CustomInput>
              </v-col>
              <v-col cols="12" md="6">
                <CustomInput :title="$t('company_website')" v-model:value="companyWeb"></CustomInput>
              </v-col>
            </v-row>

            <v-input hide-details class="text-N100 mb-1">
              {{ $t('company_address') }}
            </v-input>

            <MapLocation
              v-model="location_model"
              :factory="factory_info"
              @update_factory="$emit('factory_update')"
              edit_image
              nopan
            ></MapLocation>

            <div class="d-flex justify-end mt-8">
              <v-btn
                color="primary"
                variant="text"
                class="text-capitalize me-3"
                @click="$store.commit('editProfileToggle')"
                :disabled="loading"
              >
                {{ $t('discard') }}
              </v-btn>
              <v-btn
                color="primary"
                class="text-capitalize"
                variant="flat"
                :disabled="loading"
                :loading="loading"
                @click="storeCompany"
              >
                {{ $t('save_changes') }}
              </v-btn>
            </div>
          </v-window-item>

          <v-window-item value="profile">
            <h2 class="text-N70 font-weight-bold mb-4 text-uppercase">
              {{ $t('profile_image') }}
            </h2>

            <v-sheet class="d-flex">
              <div>
                <img v-if="user_info.profile_image" :src="user_info.profile_image" width="64" height="64" />
                <img v-else :src="require('@/assets/profile-circle.svg')" width="64" />
              </div>
              <div class="ms-3">
                {{ $t('max_file_size') }}: 5 MB
                <div class="d-md-flex mt-2">
                  <v-btn
                    variant="outlined"
                    color="primary"
                    class="text-capitalize mr-2 w-100"
                    @click="$refs.profileImage.click()"
                    :loading="profileImageLoading"
                    :disabled="profileImageLoading || removeProfileImageLoading"
                  >
                    {{ $t('change_photo') }}
                  </v-btn>
                  <input
                    ref="profileImage"
                    style="display: none"
                    type="file"
                    accept="image/*"
                    @change="loadProfileImage"
                  />
                  <v-btn
                    color="error"
                    class="text-capitalize w-100 mt-2 mt-md-0"
                    @click="removeProfileImage"
                    :loading="removeProfileImageLoading"
                    :disabled="removeProfileImageLoading || profileImageLoading"
                  >
                    {{ $t('remove') }}
                  </v-btn>
                </div>
              </div>
            </v-sheet>

            <h2 class="text-N70 my-4 text-uppercase font-weight-bold">
              {{ $t('personal_info') }}
            </h2>

            <v-row>
              <v-col cols="12" md="6" class="my-0 py-0">
                <CustomInput
                  :title="$t('display_name')"
                  :value="
                    profileData.firstName || profileData.lastName
                      ? profileData.firstName + ' ' + profileData.lastName
                      : profileData.emailAddress?.split('@')[0]
                  "
                  readonly="true"
                ></CustomInput>
              </v-col>
              <v-col cols="12" md="6" class="my-0 py-0">
                <CustomInput
                  :title="$t('email_address')"
                  :value="profileData.emailAddress"
                  readonly="true"
                ></CustomInput>
              </v-col>
              <v-col cols="12" md="6" class="my-0 py-0">
                <CustomInput :title="$t('first_name')" v-model:value="profileData.firstName"></CustomInput>
              </v-col>
              <v-col cols="12" md="6" class="my-0 py-0">
                <CustomInput :title="$t('last_name')" v-model:value="profileData.lastName"></CustomInput>
              </v-col>
            </v-row>

            <vue-tel-input
              v-model="profileData.phoneNumber"
              class="mt-4"
              :inputOptions="phoneOption"
              :class="validPhone || validPhone === undefined ? '' : 'vue-tel-input-error'"
              @validate="
                (value) => {
                  validPhone = value.valid
                  profileData.country = value
                }
              "
            ></vue-tel-input>

            <div class="d-flex justify-end mt-4">
              <v-btn
                color="primary"
                variant="text"
                class="text-capitalize me-3"
                @click="$store.commit('editProfileToggle')"
                :disabled="loading"
              >
                {{ $t('discard') }}
              </v-btn>
              <v-btn
                color="primary"
                class="text-capitalize"
                variant="flat"
                @click="storeProfile"
                :disabled="loading"
                :loading="loading"
              >
                {{ $t('save_changes') }}
              </v-btn>
            </div>
          </v-window-item>

          <v-window-item value="password">
            <h2 class="text-N70 mb-4 font-weight-bold text-uppercase">
              {{ $t('change_password') }}
            </h2>

            <v-form ref="change_password" @submit.prevent="changePassword">
              <v-text-field
                v-model="newPassword"
                :label="$t('new_password')"
                variant="outlined"
                density="compact"
                class="mt-2"
                :type="showNewPassword ? 'text' : 'password'"
                :rules="$store.state.rules.passwordRules"
                required
                v-if="!credential"
              >
                <template v-slot:append-inner>
                  <v-btn icon size="x-small" elevation="0" @click="showNewPassword = !showNewPassword">
                    <img
                      :src="showNewPassword ? require('@/assets/eye.svg') : require('@/assets/eye-disabled.svg')"
                      alt="eye"
                      width="24"
                    />
                  </v-btn>
                </template>
              </v-text-field>

              <v-text-field
                v-model="confirmNewPassword"
                :label="$t('confirm_new_password')"
                variant="outlined"
                density="compact"
                class="mt-2"
                :type="showConfirmNewPassword ? 'text' : 'password'"
                :rules="[
                  (v) => !!v || $t('confirm_password_required'),
                  (v) => newPassword === v || $t('password_must_match'),
                ]"
                v-if="!credential"
              >
                <template v-slot:append-inner>
                  <v-btn
                    icon
                    size="x-small"
                    elevation="0"
                    @click="showConfirmNewPassword = !showConfirmNewPassword"
                    class="mt-0 pt-0"
                  >
                    <img
                      :src="showConfirmNewPassword ? require('@/assets/eye.svg') : require('@/assets/eye-disabled.svg')"
                      alt="eye"
                      width="24"
                    />
                  </v-btn>
                </template>
              </v-text-field>

              <!-- <v-btn
                class="text-primary text-decoration-underline text-capitalize px-1"
                variant="text"
              >
                {{ $t('forgot_password') }}
              </v-btn> -->
              <div class="d-flex justify-end mt-4">
                <v-btn
                  color="primary"
                  variant="text"
                  class="text-capitalize me-3"
                  @click="$store.commit('editProfileToggle')"
                  :disabled="loading"
                >
                  {{ $t('discard') }}
                </v-btn>
                <v-btn
                  color="primary"
                  class="text-capitalize"
                  variant="flat"
                  type="submit"
                  :disabled="loading"
                  :loading="loading"
                >
                  {{ $t('save_changes') }}
                </v-btn>
              </div>
            </v-form>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api'
import MapLocation from '@/components/MapLocation'
import jwtDecode from 'jwt-decode'
import CustomInput from './CustomInput.vue'

export default {
  name: 'EditProfile',
  components: {
    CustomInput,
    MapLocation,
  },
  computed: {
    profileWidth() {
      if (window.innerWidth > 1264) return (window.innerWidth * 4) / 5
      return window.innerWidth - 30
    },
    isAdmin() {
      return jwtDecode(localStorage.getItem('access')).user_info.is_admin
    },
    is_admin() {
      let user_info = jwtDecode(localStorage.getItem('access')).user_info
      return user_info.is_admin
    },
    user_id() {
      return jwtDecode(localStorage.getItem('access')).user_id
    },
    phoneOption() {
      return {
        placeholder: this.$t('phone_number'),
        required: true,
      }
    },
  },
  watch: {
    '$store.state.setting'() {
      this.tab = this.$store.state.setting
    },
    '$store.state.editProfile'(value) {
      if (value) {
        this.profileData.firstName = this.user_info.first_name
        this.profileData.lastName = this.user_info.last_name
        this.profileData.emailAddress = this.user_info.email
        this.profileData.phoneNumber = this.user_info.phone

        this.companyName = this.factory_info.name
        this.companyWeb = this.factory_info.factory_site
        this.location_id = this.factory_info.location?.id
      }
    },
  },
  props: {
    user_info: {
      type: Object,
      default: {},
    },
    factory_info: {
      type: Object,
      default: {},
    },
  },
  data: () => ({
    profileData: {
      emailAddress: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      image: null,
      address_model: '',
      country: '',
    },
    validPhone: true,
    tab: null,
    companyName: '',
    companyWeb: '',
    location_model: {},
    location_id: null,
    loading: false,
    newPassword: '',
    confirmNewPassword: '',
    showNewPassword: false,
    showConfirmNewPassword: false,
    plans: [],
    factoryImageLoading: false,
    profileImageLoading: false,
    removeProfileImageLoading: false,
    removeCompanyImageLoading: false,
  }),
  mounted() {
    if (this.is_admin) {
      api.plans().then((res) => {
        this.plans = res.data
      })
    }
  },
  methods: {
    storeProfile() {
      this.loading = true
      api
        .updateProfile(
          this.user_id,
          this.profileData.firstName,
          this.profileData.lastName,
          '+' + this.profileData.country.countryCallingCode,
          this.profileData.country.number
        )
        .then(() => {
          this.loading = false
          this.$store.state.textAlert = this.$t('changes_saved')
          this.$store.state.typeAlert = 'success'
          this.$store.state.alert = true
          this.$emit('update')
        })
        .catch(() => {
          this.loading = false
        })
    },
    loadProfileImage(event) {
      this.profileImageLoading = true
      api
        .uploadProfileImage(this.user_id, event.target.files[0])
        .then(() => {
          this.$emit('update')
          this.profileImageLoading = false
        })
        .catch(() => {
          this.profileImageLoading = false
        })
    },
    removeProfileImage() {
      this.removeProfileImageLoading = true
      api
        .removeProfileImage(this.user_id)
        .then(() => {
          this.$emit('update')
          this.removeProfileImageLoading = false
        })
        .catch(() => {
          this.removeProfileImageLoading = false
        })
    },
    loadCompanyImage(event) {
      this.factoryImageLoading = true
      api
        .uploadFactoryImage(event.target.files[0], this.$store.state.factoryInfo.name)
        .then(() => {
          this.$emit('factory_update')
          this.factoryImageLoading = false
        })
        .catch(() => {
          this.factoryImageLoading = false
        })
    },
    removeComanyImage() {
      this.removeCompanyImageLoading = true
      api
        .removeComanyImage(this.$store.state.factoryInfo.name)
        .then(() => {
          this.$emit('factory_update')
          this.removeCompanyImageLoading = false
        })
        .catch(() => {
          this.removeCompanyImageLoading = false
        })
    },
    async storeCompany() {
      this.loading = true
      let res = null
      if (this.location_id) {
        api.editLocation(this.location_id, this.location_model)
      } else {
        res = await api.createLocation(`factory-${this.companyName}`, this.location_model)
      }
      api
        .updateFactory(this.companyWeb, res?.data.id || this.location_id, this.companyName, this.location_model.points)
        .then(() => {
          this.loading = false
          this.$store.state.textAlert = this.$t('changes_saved')
          this.$store.state.typeAlert = 'success'
          this.$store.state.alert = true
          this.$emit('factory_update')
          this.$store.state.editProfile = false
        })
        .catch(() => {
          this.loading = false
        })
    },
    changePassword() {
      this.$refs.change_password.validate().then((value) => {
        if (value.valid) {
          this.loading = true
          api
            .changePassword(this.user_id, this.newPassword, this.confirmNewPassword)
            .then(() => {
              this.$store.state.textAlert = this.$t('changes_saved')
              this.$store.state.typeAlert = 'success'
              this.$store.state.alert = true
              this.loading = false
            })
            .catch(() => (this.loading = false))
        }
      })
    },
    upgrade_plan(plan) {
      plan.loading = true
      api
        .upgrade_plan(plan.id)
        .then((res) => {
          plan.loading = false
        })
        .catch(() => (plan.loading = false))
    },
    clear() {
      this.profileData = {}
    },
  },
}
</script>

<style>
.vue-tel-input {
  height: 40px;
  margin-top: 6px;
  border: 1px solid #aaa !important;
  border-radius: 4px !important;
  box-shadow: none !important;
}
.vue-tel-input-error {
  border: 1px solid #fc5a5a !important;
}
</style>
